import { useEffect, useMemo } from 'react'
import { useLedgerStore, useDebtStore } from '@/store'
import { computeLedgerTotals, resolveEntryAllocations } from '@/engine/ledger'
import { useRecurringExpenses } from '@/hooks/useRecurringExpenses'

/**
 * Primary facade for the Cash Flow module — gives the UI everything it needs
 * about the active month plus all CRUD actions.
 *
 * Components should never reach into useLedgerStore directly. This hook is
 * the contract.
 */
export function useLedger() {
  const debts = useDebtStore((s) => s.debts)
  const debtsInitialized = useDebtStore((s) => s.isInitialized)

  const incomeSources = useLedgerStore((s) => s.incomeSources)
  const ledgerEntries = useLedgerStore((s) => s.ledgerEntries)
  const activeMonth = useLedgerStore((s) => s.activeMonth)
  const isInitialized = useLedgerStore((s) => s.isInitialized)
  const initError = useLedgerStore((s) => s.initError)
  const writeError = useLedgerStore((s) => s.writeError)
  const clearWriteError = useLedgerStore((s) => s.clearWriteError)
  const syncNotice = useLedgerStore((s) => s.syncNotice)
  const clearSyncNotice = useLedgerStore((s) => s.clearSyncNotice)
  const initialize = useLedgerStore((s) => s.initialize)

  // Actions
  const addIncomeSource = useLedgerStore((s) => s.addIncomeSource)
  const updateIncomeSource = useLedgerStore((s) => s.updateIncomeSource)
  const removeIncomeSource = useLedgerStore((s) => s.removeIncomeSource)
  const setActiveMonth = useLedgerStore((s) => s.setActiveMonth)
  const setIncomeAmount = useLedgerStore((s) => s.setIncomeAmount)
  const setMonthMinimumPayment = useLedgerStore((s) => s.setMonthMinimumPayment)
  const clearMonthMinimumPaymentOverride = useLedgerStore((s) => s.clearMonthMinimumPaymentOverride)
  const setPlannedPayment = useLedgerStore((s) => s.setPlannedPayment)
  const setActualPayment = useLedgerStore((s) => s.setActualPayment)
  const addPayment = useLedgerStore((s) => s.addPayment)
  const setEndingBalanceOverride = useLedgerStore((s) => s.setEndingBalanceOverride)
  const applyMinimums = useLedgerStore((s) => s.applyMinimums)
  const applyAvalancheSurplus = useLedgerStore((s) => s.applyAvalancheSurplus)
  const applySnowballSurplus = useLedgerStore((s) => s.applySnowballSurplus)
  const resetMonthFromRegistry = useLedgerStore((s) => s.resetMonthFromRegistry)
  const closeCurrentMonth = useLedgerStore((s) => s.closeCurrentMonth)
  const startNextMonth = useLedgerStore((s) => s.startNextMonth)
  const skipCurrentMonth = useLedgerStore((s) => s.skipCurrentMonth)
  const reopenMonth = useLedgerStore((s) => s.reopenMonth)

  // Initialize once debts are loaded (we need them to bootstrap the first entry)
  useEffect(() => {
    if (debtsInitialized && !isInitialized) {
      void initialize(debts)
    }
  }, [debtsInitialized, isInitialized, initialize, debts])

  const activeEntry = useMemo(
    () => ledgerEntries.find((e) => e.month === activeMonth),
    [ledgerEntries, activeMonth]
  )

  // Resolve essentials for the active entry:
  //   - PLANNING entries: live from recurring expenses (this-month actual).
  //     Falls back to the steady-state average if nothing bills this month, so
  //     the floor doesn't artificially collapse in calm months.
  //   - CLOSED entries: snapshot frozen at close-time.
  // This is the "Plan is live, history is frozen" pattern, applied to essentials.
  const { thisMonthEssentials, essentialAvgMonthly } = useRecurringExpenses(activeMonth)
  const resolvedEssentials = useMemo(() => {
    if (!activeEntry) return 0
    if (activeEntry.status === 'closed') {
      return activeEntry.essentialExpenses ?? 0
    }
    if (thisMonthEssentials !== null && thisMonthEssentials > 0) {
      return thisMonthEssentials
    }
    return essentialAvgMonthly
  }, [activeEntry, thisMonthEssentials, essentialAvgMonthly])

  const activeEntryWithTotals = useMemo(
    () => (activeEntry ? computeLedgerTotals(activeEntry, debts, resolvedEssentials) : null),
    [activeEntry, debts, resolvedEssentials]
  )

  /**
   * Resolved allocations for the active entry — pulls live registry values for
   * planning months and snapshot values for closed. UI components should
   * display from this list rather than `activeEntry.allocations` directly.
   */
  const resolvedAllocations = useMemo(
    () => (activeEntry ? resolveEntryAllocations(activeEntry, debts) : []),
    [activeEntry, debts]
  )

  // Sorted month list for navigation (oldest → newest)
  const allMonths = useMemo(() => ledgerEntries.map((e) => e.month), [ledgerEntries])

  return {
    // Data
    incomeSources,
    activeMonth,
    activeEntry,
    activeEntryWithTotals,
    resolvedAllocations,
    allMonths,
    isInitialized,
    initError,
    writeError,
    clearWriteError,
    syncNotice,
    clearSyncNotice,

    // Income source CRUD
    addIncomeSource,
    updateIncomeSource,
    removeIncomeSource,

    // Navigation
    setActiveMonth,

    // Editing (active entry)
    setIncomeAmount,
    setMonthMinimumPayment,
    clearMonthMinimumPaymentOverride,
    setPlannedPayment,
    setActualPayment,
    addPayment,
    setEndingBalanceOverride,

    // Quick-fill (auto-pass current debts so callers don't have to)
    applyMinimums: () => applyMinimums(debts),
    applyAvalancheSurplus: (surplus: number) => applyAvalancheSurplus(debts, surplus),
    applySnowballSurplus: (surplus: number) => applySnowballSurplus(debts, surplus),

    // Reset
    resetMonthFromRegistry: () => resetMonthFromRegistry(debts),

    // Lifecycle
    // closeCurrentMonth captures the resolved essentials at close-time so the
    // closed entry's snapshot reflects the live recurring-expenses sum.
    closeCurrentMonth: () => closeCurrentMonth(debts, resolvedEssentials),
    startNextMonth: () => startNextMonth(debts),
    skipCurrentMonth: () => skipCurrentMonth(debts),
    reopenMonth,
  }
}
